import { useState, useRef, useEffect } from "react";
import PlanDeCuentasModal from "../../../../../../modals/modalPlanDeCuentas/modalPlanDeCuentas";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { PlanCuentasApi } from "../../../../../../../../api/planCuentas.api";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RecyclingIcon from '@mui/icons-material/Recycling';

/**
 * Estructura las carpetas en jerarquías basadas en `codigoFinal`.
 * @param {Array} data - Lista de elementos del plan de cuentas.
 * @returns {Array} Lista jerarquizada de carpetas.
 */
export const structureFolders = (data, showDeleted = false) => {
  const folderMap = new Map();

  // Filtrar cuentas según showDeleted y excluir "Cuenta Otros"
  const filteredData = data.filter(
    (item) => (showDeleted || item.isDeleted === "0") && item.nameFinal !== "Cuenta Otros"
  );

  // Crear el mapa de carpetas
  filteredData.forEach((item) => {
    folderMap.set(item.codigoFinal, { ...item, subfolders: [] });
  });

  const rootFolders = [];

  // Construir la jerarquía
  filteredData.forEach((item) => {
    const level = item.codigoFinal.split("-").filter((num) => num !== "0").length;
    const parentCode = getParentCode(item.codigoFinal, level);

    if (parentCode) {
      const parentFolder = folderMap.get(parentCode);
      if (parentFolder) {
        parentFolder.subfolders.push(folderMap.get(item.codigoFinal));
      } else {
        rootFolders.push(folderMap.get(item.codigoFinal));
      }
    } else {
      rootFolders.push(folderMap.get(item.codigoFinal));
    }
  });

  return rootFolders;
};
/**
 * Obtiene el código del padre basado en el nivel.
 * @param {string} codigoFinal - Código actual de la cuenta.
 * @param {number} level - Nivel del código actual.
 * @returns {string|null} Código del padre, o null si no existe.
 */
export const getParentCode = (codigoFinal) => {
  const parts = codigoFinal.split("-").filter(p => p !== "0");
  
  // Truncar a máximo 4 segmentos y generar código padre
  if (parts.length > 4) parts.length = 4; // Ej: "3-1-5-34" → "3-1-5-34" (pero se ajusta en el siguiente paso)
  
  const parentParts = parts.slice(0, -1); // Remover el último segmento
  if (parentParts.length === 0) return null;
  
  return parentParts.join("-") + "-0".repeat(4 - parentParts.length);
};

const FolderItem = ({ folder, level = 1, isEditMode, businessId, refreshData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState("");

  const toggleFolder = () => {
    if (level < 4) {
      setIsOpen((prevState) => !prevState);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const handleSave = async (editedFolderName) => {
    const planCuentasApi = new PlanCuentasApi();
    try {
      if (folder.cuentaCustomId !== null) {
        // Usar el endpoint para cuentas personalizadas
        await planCuentasApi.updateCustomAccount(
          businessId,
          folder.cuentaCustomId, // Usar cuentaCustomId como ID
          editedFolderName,
          false // No eliminar la cuenta
        );
      } else {
        // Usar el endpoint para cuentas estándar
        await planCuentasApi.UpdateCuenta(
          businessId,
          folder.cuentaContableId, // Usar cuentaContableId como ID
          editedFolderName
        );
      }

      setMessage("¡Cambios guardados exitosamente! 🎉");

      if (refreshData) refreshData(); // Refrescar datos tras guardar
    } catch (error) {
      console.error("Error al actualizar la cuenta:", error);
      setMessage("Hubo un error al guardar los cambios.");
    } finally {
      setTimeout(() => setMessage(""), 3000); // Limpia el mensaje tras 3 segundos
    }
  };

  const isMainFolder = level === 1;

  const indent = level * 12;

  return (
    <div
      style={{ marginLeft: `${indent}px`, marginTop: "8px" }}
      className={isMainFolder ? "bg-border/40 rounded-card p-3 border border-border" : ""}
    >
      <FolderHeader
        folderName={folder.nameFinal}
        folderId={folder.cuentaCustomId ?? folder.cuentaContableId}
        folderCode={folder.codigoFinal} // Pasar el código de la cuenta
        businessId={businessId}
        isCustom={folder.cuentaCustomId !== null}
        isOpen={isOpen}
        toggleFolder={toggleFolder}
        level={level}
        isEditMode={isEditMode}
        setMessage={setMessage}
        refreshData={refreshData}
        onAddClick={openModal}
        onSave={handleSave}
        isDeleted={folder.isDeleted}
      />

      {isOpen && level < 4 && folder.subfolders?.length > 0 && (
        <FolderList
          subfolders={folder.subfolders}
          level={level + 1}
          isEditMode={isEditMode}
          businessId={businessId}
          refreshData={refreshData}
        />
      )}

      {message && (
        <div className="mt-2 p-2 bg-success/10 border border-success/20 rounded text-success text-xs">
          {message}
        </div>
      )}

      {isModalOpen && (
        <PlanDeCuentasModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          selectedFolderCode={folder.codigoFinal} // Código del padre
          businessId={businessId} // ID del negocio
          refreshData={refreshData} // Refrescar datos tras agregar
        />
      )}
    </div>
  );
};

const FolderHeader = ({
  folderName,
  folderId,
  folderCode, // Código de la cuenta, como 0-0-0-0
  businessId,
  isCustom, // Indica si la cuenta ya fue personalizada
  isOpen,
  toggleFolder,
  level,
  onAddClick,
  isEditMode,
  setMessage,
  refreshData,
  isDeleted,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedFolderName, setEditedFolderName] = useState(folderName);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const planCuentasApi = new PlanCuentasApi();
  const editRef = useRef(null); // Referencia para detectar clics dentro del componente

  // Manejar clics fuera del componente para desactivar el modo de edición
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editRef.current && !editRef.current.contains(event.target)) {
        setIsEditing(false); // Salir del modo de edición
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside); // Limpieza
    };
  }, [isEditing]);

  const handleReactivateClick = async (e) => {
    e.stopPropagation();
    try {
      await planCuentasApi.reactivateAccounts(businessId, [folderId]); // Restaurar cuenta
      if (refreshData) refreshData(); // Refrescar datos tras la restauración
    } catch (error) {
      console.error("Error al restaurar la cuenta:", error);
      setMessage("Hubo un error al restaurar la cuenta.");
    } finally {
      setTimeout(() => setMessage(""), 3000); // Limpia el mensaje tras 3 segundos
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true); // Activar modo edición
  };

  const handleSaveClick = async (e) => {
    e.stopPropagation();
    setIsSaving(true);

    try {
      if (isCustom) {
        await planCuentasApi.updateCustomAccount(businessId, folderId, editedFolderName, false);
      } else {
        await planCuentasApi.UpdateCuenta(businessId, folderId, editedFolderName);
      }

      if (refreshData) refreshData(); // Refrescar datos tras guardar
      setIsEditing(false);
    } catch (error) {
      console.error("Error al actualizar la cuenta:", error);
      setMessage("Hubo un error al guardar los cambios.");
    } finally {
      setTimeout(() => setMessage(""), 3000); // Limpia el mensaje tras 3 segundos
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await planCuentasApi.markAccountAsDeleted(businessId, folderCode); // Marcar como eliminada
      if (refreshData) refreshData(); // Refrescar datos tras eliminar
    } catch (error) {
      console.error("Error al eliminar la cuenta:", error);
      setMessage("Hubo un error al eliminar la cuenta.");
    } finally {
      setTimeout(() => setMessage(""), 3000);
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };


  const handleInputChange = (e) => {
    setEditedFolderName(e.target.value); // Actualizar nombre editado
  };

  const shouldShowAddButton = isEditMode && level === 3;
  const shouldShowDelButton = isEditMode && isDeleted === "0" && ( level === 4); // Ocultar "Eliminar" para cuentas eliminadas
  const shouldShowEditOrSaveButton = isEditMode && ( level === 4);
  const shouldShowRestoreButton = isEditMode && isDeleted === "1"; // Mostrar botón de restaurar si la cuenta está eliminada

  const folderStyle = isDeleted === "1" ? "opacity-60 line-through text-red-500" : "text-gray-800"; // Estilo para carpetas eliminadas

  return (
    <>
      <div
        className={`group flex items-center justify-between rounded-card px-2 py-1.5 transition-colors ${
          level === 4 ? "cursor-default" : "cursor-pointer hover:bg-border/40"
        }`}
        ref={editRef} // Referencia al contenedor
        onClick={() => toggleFolder()}
      >
        <div className="flex items-center gap-1">
          {level === 4 ? (
            <HorizontalRuleIcon fontSize="small" className="text-muted" />
          ) : isOpen ? (
            <KeyboardArrowDownIcon fontSize="small" className="text-muted rotate-90" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" className="text-muted" />
          )}

          {isEditing ? (
            <input
              type="text"
              value={editedFolderName}
              onChange={handleInputChange}
              maxLength={50}
              className="ml-2 text-[0.75rem] font-medium text-text border-b border-border focus:outline-none focus:border-primary bg-transparent"
              autoFocus
            />
          ) : (
            <span className={`ml-1 text-[0.75rem] font-medium ${folderStyle}`}>
              {folderName}
              {isEditMode && (  // Solo muestra el código si está en modo edición
                <span className="text-muted text-[0.65rem]"> ({folderCode})</span>
              )}
            </span>
          )}
        </div>

        <div className="flex items-center justify-end gap-1">
          {shouldShowAddButton && (
            <button
              className="p-1 rounded-full text-muted hover:text-primary hover:bg-border/50 transition-all duration-200"
              title="Add folder"
              onClick={(e) => {
                e.stopPropagation();
                onAddClick();
              }}
            >
              <AddIcon fontSize="small" />
            </button>
          )}
          {shouldShowEditOrSaveButton &&
            (isEditing ? (
              <button
                className={`p-1 rounded-full text-muted hover:text-primary hover:bg-border/50 transition-all duration-200 ${
                  isSaving ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title="Guardar"
                onClick={(e) => handleSaveClick(e)}
                disabled={isSaving}
              >
                <SaveIcon fontSize="small" />
              </button>
            ) : (
              <button
                className="p-1 rounded-full text-muted hover:text-primary hover:bg-border/50 transition-all duration-200"
                title="Editar"
                onClick={handleEditClick}
              >
                <EditIcon fontSize="small" />
              </button>
            ))}
          {shouldShowDelButton && (
            <button
              className="p-1 rounded-full text-muted hover:text-danger hover:bg-border/50 transition-all duration-200"
              title="Quitar"
              onClick={(e) => handleDeleteClick(e)}
            >
              <DeleteForeverIcon fontSize="small" />
            </button>
          )}
          {shouldShowRestoreButton && (
            <button
              className="p-1 rounded-full text-muted hover:text-success hover:bg-border/50 transition-all duration-200"
              title="Restaurar"
              onClick={(e) => handleReactivateClick(e)}
            >
              <RecyclingIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-surface rounded-card shadow-card p-6 w-[320px] border border-border">
            <h4 className="text-base font-semibold mb-2 text-text">Quitar cuenta</h4>
            <p className="text-xs text-muted mb-4">
              ¿Seguro que quieres quitar la cuenta &quot;{folderName}&quot; ({folderCode})?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 rounded-full border border-border text-text hover:bg-border/40 transition text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteModalOpen(false);
                }}
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 rounded-full bg-danger text-white hover:bg-danger/90 transition disabled:opacity-60 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmDelete();
                }}
                disabled={isDeleting}
              >
                {isDeleting ? "Quitando..." : "Quitar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
const FolderList = ({ subfolders, level, isEditMode, businessId, refreshData }) => {
  return (
    <div>
      {subfolders.map((subfolder) => (
        <FolderItem
          key={subfolder.cuentaContableId} // Asegúrate de usar una clave única
          folder={subfolder}
          level={level}
          isEditMode={isEditMode}
          businessId={businessId} // Pasa el businessId a las subcarpetas
          refreshData={refreshData} // Pasa refreshData a las subcarpetas
        />
      ))}
    </div>
  );
};

export default FolderItem;
